import {
  setCertificateInstances,
  setCertificateTypes,
  setCertificates,
  setCompanies,
  setLoggings,
  setUsers,
  type Certificate,
  type CertificateInstance,
  type CertificateType,
  type Company,
  type Logging,
  type User,
  setIsCompaniesRequested,
  setIsCertificateInstancesRequested,
  type EnrichedCertificate,
  setEnrichedCertificates,
} from '../Store/DataSlice'
import { subscribe } from '../Store/State'
import { type RootState, store } from '../Store/Store'
import { getAuthorizationHeader } from './Authentication'
import { createSelector } from 'reselect'
import { runWebWorker } from './Webworker'
import { webWorkerCalculateEnrichedCertificates } from './WebworkerFunctions'

export function orchestrateData() {
  subscribe(
    (state: RootState) => state.data.isCompaniesRequested,
    (isCompaniesRequested, wasCompaniesRequested) => {
      if (isCompaniesRequested && !wasCompaniesRequested) {
        getItems<Company>(
          'https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/company-list'
        )
          .then((items) => {
            store.dispatch(setCompanies(items))
          })
          .catch((err) => {
            console.warn(`fetch company-list failed: `, err)
          })
      }
    }
  )
  subscribe(
    (state: RootState) => state.data.isCertificateInstancesRequested,
    (isCertificateInstancesRequested, wasCertificateInstancesRequested) => {
      if (
        isCertificateInstancesRequested &&
        !wasCertificateInstancesRequested
      ) {
        getItems<CertificateInstance>(
          'https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-instance-list'
        )
          .then((items) => {
            store.dispatch(setCertificateInstances(items))
          })
          .catch((err) => {
            console.warn(`fetch certificate-instance-list failed: `, err)
          })
      }
    }
  )
  subscribe(
    (state: RootState) => state.data.isUsersRequested,
    (isUsersRequested, wasUsersRequested) => {
      if (isUsersRequested && !wasUsersRequested) {
        getItems<User>(
          'https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/user-list'
        )
          .then((items) => {
            store.dispatch(setUsers(items))
          })
          .catch((err) => {
            console.warn(`fetch user-list failed: `, err)
          })
      }
    }
  )

  subscribe(
    (state: RootState) => state.data.isCertificatesRequested,
    (isCertificatesRequested, wasCertificatesRequested) => {
      if (isCertificatesRequested && !wasCertificatesRequested) {
        getItems<Certificate>(
          'https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-list'
        )
          .then((items) => {
            store.dispatch(setCertificates(items))
          })
          .catch((err) => {
            console.warn(`fetch company-list failed: `, err)
          })
        // if certificates are requested, make sure certificate instances & companies are fetched at least once
        if (
          !store.getState().data.isCertificateInstancesRequested &&
          store.getState().data.certificateInstances.length === 0
        )
          store.dispatch(setIsCertificateInstancesRequested(true))
        if (
          !store.getState().data.isCompaniesRequested &&
          store.getState().data.companies.length === 0
        )
          store.dispatch(setIsCompaniesRequested(true))
      }
    }
  )
  subscribe(
    (state: RootState) => state.data.isCertificateTypesRequested,
    (isCertificateTypesRequested, wasCertificateTypesRequested) => {
      if (isCertificateTypesRequested && !wasCertificateTypesRequested) {
        getItems<CertificateType>(
          'https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-type-list'
        )
          .then((items) => {
            store.dispatch(setCertificateTypes(items))
          })
          .catch((err) => {
            console.warn(`fetch certificate-type-list failed: `, err)
          })
      }
    }
  )
  subscribe(
    (state: RootState) => state.data.isLoggingsRequested,
    (isLoggingsRequested, wasLoggingsRequested) => {
      if (isLoggingsRequested && !wasLoggingsRequested) {
        getItems<Logging>(
          'https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/logging-list'
        )
          .then((items) => {
            store.dispatch(setLoggings(items))
          })
          .catch((err) => {
            console.warn(`fetch logging-list failed: `, err)
          })
      }
    }
  )

  let lastWorker: Worker | undefined
  subscribe(
    createSelector(
      (state: RootState) => state.data.certificates,
      (state: RootState) => state.data.companies,
      (state: RootState) => state.data.certificateInstances,
      (certificates, companies, certificateInstances) => ({
        certificates,
        companies,
        certificateInstances,
      })
    ),
    ({ certificates, companies, certificateInstances }) => {
      lastWorker?.terminate()
      lastWorker = runWebWorker(webWorkerCalculateEnrichedCertificates, {
        certificates,
        companies,
        certificateInstances,
      })
      lastWorker.onmessage = (ev) => {
        store.dispatch(
          setEnrichedCertificates(ev.data as EnrichedCertificate[])
        )
      }
    }
  )
}

async function getItems<T>(url: string, lastEvaluatedKey?: unknown) {
  return await new Promise<T[]>((resolve, reject) => {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthorizationHeader(),
      },
      body: JSON.stringify({
        lastEvaluatedKey,
      }),
    })
      .then(async (response) => await response.json())
      .then(
        (response: {
          success: true
          items: T[]
          lastEvaluatedKey: unknown
        }) => {
          if (response.lastEvaluatedKey !== undefined) {
            getItems<T>(url, response.lastEvaluatedKey)
              .then((items: T[]) => {
                resolve([...response.items, ...items])
              })
              .catch((err) => {
                reject(err)
              })
          } else {
            resolve(response.items)
          }
        }
      )
      .catch((err) => {
        reject(err)
      })
  })
}
